import {
  type OnDestroy,
  type OnInit,
  Component,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  INITIAL_LOAD_IDLE_DEFAULTS,
  InitialDataLoaderService,
} from '@core/services/initial-data-loader.service';
import { LayoutStoreService } from '@core/services/layout-store.service';
import { SidenavComponent } from '@layouts/components/sidenav/sidenav.component';
import { TopbarComponent } from '@layouts/components/topbar/topbar';
import { FullscreenLoadingComponent } from '@shared/components/fullscreen-loading/fullscreen-loading.component';
import { type Subscription, debounceTime, fromEvent } from 'rxjs';

@Component({
  selector: 'app-vertical-layout',
  imports: [RouterOutlet, SidenavComponent, TopbarComponent, FullscreenLoadingComponent],
  templateUrl: './vertical-layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``,
})
export class VerticalLayoutComponent implements OnInit, OnDestroy {
  public layout = inject(LayoutStoreService);
  private readonly router = inject(Router);
  private readonly initialDataLoaderService = inject(InitialDataLoaderService);

  resizeSubscription!: Subscription;
  initialLoadSubscription?: Subscription;
  isWaitingForInitialData = false;

  ngOnInit(): void {
    this.onResize();

    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(200))
      .subscribe(() => this.onResize());
  }

  onResize(): void {
    const width = window.innerWidth;

    if (width <= 1140) {
      this.layout.setSidenavSize('offcanvas');
    } else {
      this.layout.setSidenavSize('default');
    }
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
    this.initialLoadSubscription?.unsubscribe();
  }

  onRouteActivated(): void {
    this.waitForInitialRouteDataAndHideLoader(this.router.url);
  }

  private waitForInitialRouteDataAndHideLoader(url: string): void {
    if (
      !this.layout.isLoading ||
      this.isDashboardRoute(url) ||
      this.isWaitingForInitialData ||
      !this.initialDataLoaderService.isTrackingEnabled()
    ) {
      return;
    }

    const trackingSessionId = this.initialDataLoaderService.getTrackingSessionId();
    this.isWaitingForInitialData = true;
    this.initialLoadSubscription?.unsubscribe();
    this.initialLoadSubscription = this.initialDataLoaderService
      .waitUntilSettledIdle({
        quietWindowMs: INITIAL_LOAD_IDLE_DEFAULTS.quietWindowMs,
        maxWaitMs: INITIAL_LOAD_IDLE_DEFAULTS.maxWaitMs,
        sessionId: trackingSessionId,
      })
      .subscribe(() => {
        if (trackingSessionId !== this.initialDataLoaderService.getTrackingSessionId()) {
          return;
        }

        this.layout.setIsLoading(false);
        this.initialDataLoaderService.stopTracking();
      });

    this.initialLoadSubscription.add(() => {
      this.isWaitingForInitialData = false;
    });
  }

  private isDashboardRoute(url: string): boolean {
    const withoutQuery = url.split('?')[0] ?? '';
    const normalizedUrl = withoutQuery.split('#')[0] ?? '';
    return normalizedUrl === '/dashboard' || normalizedUrl.startsWith('/dashboard/');
  }
}
